import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin, RefreshCw } from 'lucide-react';
import SideBar from '../common/SideBar';
import { DEFAULT_BACK_OFFICE_ITEMS } from '../../helpers/constants';
import { ENDPOINTS } from '../../helpers/constants';
import { makeRESTApiRequest } from '../../helpers/api';
import { toast } from 'sonner';
import { useHistory, useLocation } from 'react-router-dom';
import QBIcon from '../icons/QBIcon';

const BackOfficeSidebar = ({
  title,
  storageKey,
  openCountyMunicipalityModal,
  activeItemId,
  setActiveItemId,
  onBeforeItemSelect,
  isMobile = false,
  mobileOpen = false,
  onRequestClose = () => {},
  setCollapsed = () => {},
  children
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isQuickBooksConnected, setIsQuickBooksConnected] = useState(localStorage.getItem('qbConn') === '1');
  const [currentUserInfo] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const hasFinancialIntelligenceAccess = currentUserInfo?.hasFinancialIntelligenceAccess || false;

  const persistQuickBooksConnection = useCallback(connected => {
    const isConnected = Boolean(connected);
    setIsQuickBooksConnected(isConnected);
    localStorage.setItem('qbConn', isConnected ? '1' : '0');
  }, []);

  const checkQuickBooksConnection = useCallback(async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.QUICKBOOKS_CHECK_CONNECTION,
      method: 'GET'
    });
    if (!error) {
      persistQuickBooksConnection(response?.connected);
    }
  }, [persistQuickBooksConnection]);

  useEffect(() => {
    checkQuickBooksConnection();
  }, [checkQuickBooksConnection]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const qb = params.get('qb');
    if (qb === 'connected') {
      toast.success('QuickBooks connected successfully.');
      persistQuickBooksConnection(true);
      checkQuickBooksConnection();

      history.replace(location.pathname);
      return;
    }
    if (qb === 'error') {
      const message = params.get('message');
      toast.error(message ? decodeURIComponent(message) : 'QuickBooks connection failed.');
      checkQuickBooksConnection();
      history.replace(location.pathname);
    }
  }, [checkQuickBooksConnection, history, location.pathname, location.search, persistQuickBooksConnection]);

  const onConnectQuickBooks = useCallback(async () => {
    const { response, error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.QUICKBOOKS_AUTH,
      method: 'GET'
    });
    if (error) {
      toast.error(error);
      return;
    }
    if (response?.redirectUrl) {
      window.location.href = response.redirectUrl;
    } else {
      toast.error('Could not start QuickBooks connection.');
    }
  }, []);

  const items = useMemo(() => DEFAULT_BACK_OFFICE_ITEMS, []);
  const footerItems = useMemo(
    () => [
      ...(hasFinancialIntelligenceAccess
        ? [
            {
              id: isQuickBooksConnected ? 'quickbooks-connected' : 'quickbooks',
              name: isQuickBooksConnected ? (
                <span className="d-inline-flex flex-column" style={{ lineHeight: 1.1 }}>
                  <span className="smallFont">QuickBooks</span>
                  <span className="xtinyFont text-success">● Connected</span>
                </span>
              ) : (
                'Connect QuickBooks'
              ),
              icon: QBIcon,
              secondaryIcon: isQuickBooksConnected ? (
                <span title="Refresh QuickBooks">
                  <RefreshCw size={14} />
                </span>
              ) : (
                undefined
              ),
              onClick: onConnectQuickBooks
            }
          ]
        : []),
      {
        id: 'manage-counties',
        name: 'Manage Counties',
        icon: MapPin,
        onClick: openCountyMunicipalityModal
      }
    ],
    [isQuickBooksConnected, onConnectQuickBooks, openCountyMunicipalityModal]
  );

  const handleSidebarItemSelect = useCallback(
    nextId => {
      if (!nextId || nextId === activeItemId) return;

      const blocked = onBeforeItemSelect?.(nextId);
      if (blocked) return;

      setActiveItemId(nextId);
      onRequestClose();
    },
    [activeItemId, onBeforeItemSelect, onRequestClose, setActiveItemId]
  );

  return (
    <SideBar
      children={children}
      items={items}
      title={title}
      storageKey={storageKey}
      onCollapsedChange={setCollapsed}
      footerItems={footerItems}
      activeItemId={activeItemId}
      onItemSelect={handleSidebarItemSelect}
      isMobile={isMobile}
      mobileOpen={mobileOpen}
      onRequestClose={onRequestClose}
    />
  );
};

export default BackOfficeSidebar;
